// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useInstitutionData } from '@shared/advisory/aggregation/providers/institutions-provider';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

const INSTITUTION_PARAM = 'institution';

export const useInstitutions = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { clients, clientId, client } = useInstitutionData();

  const institutionIds = useCallback(
    (client: (typeof clients)[number]) => client.institutions.map(inst => inst.id),
    [],
  );

  const defaultTabValue = useCallback(
    (client: (typeof clients)[number]) => {
      const ids = institutionIds(client);

      const hasInstitutions =
        client.institutions.filter(i => i.id !== 'ALL').length > 0;

      const fallback = hasInstitutions ? 'ALL' : 'none';

      const tabFromUrl = searchParams.get(INSTITUTION_PARAM);

      if (tabFromUrl === null || tabFromUrl === '') return fallback;

      return ids.includes(tabFromUrl) ? tabFromUrl : fallback;
    },
    [searchParams, institutionIds],
  );

  const onTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);

          if (value === 'none') {
            next.delete(INSTITUTION_PARAM);
          } else {
            next.set(INSTITUTION_PARAM, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { t, clients, clientId, defaultTabValue, client, onTabChange };
};
